<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  reasons: { type: Array, required: true },
  anchor: { type: Object, default: null },
  inModal: { type: Boolean, default: false },
  containerRect: { type: Object, default: null },
  color: {
    type: String,
    default: 'ruby',
    validator: v => ['ruby', 'amber'].includes(v),
  },
});
const emit = defineEmits(['confirm', 'cancel', 'create']);

const { t } = useI18n();

const HIGHLIGHT_CLASSES = {
  ruby: 'text-[#CA244E] font-medium bg-n-ruby-2',
  amber: 'text-n-amber-11 font-medium bg-n-amber-2',
};

const highlightClass = computed(
  () => HIGHLIGHT_CLASSES[props.color] ?? HIGHLIGHT_CLASSES.ruby
);

const selectedReason = ref('');
const searchQuery = ref('');
const highlightedIndex = ref(0);
const dropdownRef = ref(null);

const filteredReasons = computed(() => {
  const q = searchQuery.value.toLowerCase().trim();
  if (!q) return props.reasons;
  return props.reasons.filter(r => r.toLowerCase().includes(q));
});

const canCreate = computed(() => {
  const q = searchQuery.value.trim();
  if (!q) return false;
  return !props.reasons.some(r => r.toLowerCase() === q.toLowerCase());
});

watch(filteredReasons, () => {
  highlightedIndex.value = 0;
});

function createAndSelect() {
  const newReason = searchQuery.value.trim();
  selectedReason.value = newReason;
  emit('create', newReason);
  emit('confirm', newReason);
}

const style = ref({});

function computeStyle() {
  // Modo modal: cobre exatamente o painel de conteúdo usando position fixed
  if (props.inModal) {
    const cr = props.containerRect;
    if (cr) {
      const half = Math.floor(cr.width / 2);
      style.value = {
        top: `${cr.top}px`,
        left: `${cr.left + half}px`,
        width: `${half}px`,
        height: `${cr.height}px`,
        overflowY: 'auto',
      };
    } else {
      style.value = {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        width: '320px',
        maxHeight: '400px',
        overflowY: 'auto',
      };
    }
    return;
  }

  // Modo card: sempre abaixo do anchor, acima se não couber
  const a = props.anchor;
  if (!a) {
    style.value = {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%,-50%)',
    };
    return;
  }

  const dropW = 256;
  let left = a.left;
  if (left + dropW > window.innerWidth - 8)
    left = window.innerWidth - dropW - 8;
  left = Math.max(8, left);

  const minDropH = 200;
  const spaceBelow = window.innerHeight - a.bottom - 8;
  const spaceAbove = a.top - 8;

  if (spaceBelow < minDropH) {
    style.value = {
      bottom: `${window.innerHeight - a.top + 4}px`,
      left: `${left}px`,
      width: `${Math.max(a.width, dropW)}px`,
      maxHeight: `${Math.min(spaceAbove, 400)}px`,
      overflowY: 'auto',
    };
    return;
  }

  style.value = {
    top: `${a.bottom + 4}px`,
    left: `${left}px`,
    width: `${Math.max(a.width, dropW)}px`,
    maxHeight: `${spaceBelow}px`,
    overflowY: 'auto',
  };
}

function onDocMousedown(e) {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target)) {
    emit('cancel');
  }
}

function onKeydown(e) {
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    highlightedIndex.value = Math.min(
      highlightedIndex.value + 1,
      filteredReasons.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = filteredReasons.value[highlightedIndex.value];
    if (item) {
      emit('confirm', item);
    } else if (canCreate.value) {
      createAndSelect();
    }
  } else if (e.key === 'Backspace') {
    searchQuery.value = searchQuery.value.slice(0, -1);
  } else if (e.key === 'Escape') {
    emit('cancel');
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
    searchQuery.value += e.key;
    highlightedIndex.value = 0;
  }
}

onMounted(() => {
  nextTick(computeStyle);
  window.addEventListener('keydown', onKeydown);
  window.addEventListener('mousedown', onDocMousedown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown);
  window.removeEventListener('mousedown', onDocMousedown);
});
</script>

<template>
  <Teleport to="body">
    <div
      ref="dropdownRef"
      class="fixed z-[10010] bg-n-background border border-n-weak rounded-xl shadow-xl py-1 min-w-[200px]"
      :style="style"
    >
      <p v-if="searchQuery" class="px-3 pt-1 pb-0.5 text-xs text-n-slate-10">
        {{ searchQuery }}
      </p>
      <button
        v-for="(reason, idx) in filteredReasons"
        :key="reason"
        class="w-full text-left px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-1 transition-colors focus:outline-none"
        :class="{ [highlightClass]: highlightedIndex === idx }"
        @mouseenter="highlightedIndex = idx"
        @click="emit('confirm', reason)"
      >
        {{ reason }}
      </button>
      <button
        v-if="canCreate"
        class="w-full text-left px-3 py-2 text-sm text-n-brand hover:bg-n-alpha-1 transition-colors focus:outline-none"
        @click="createAndSelect"
      >
        {{
          `+ ${t('KANBAN.LOSS_REASON_MODAL.CREATE_PREFIX')} "${searchQuery.trim()}"`
        }}
      </button>
      <p
        v-if="filteredReasons.length === 0 && !canCreate"
        class="px-3 py-2 text-sm text-n-slate-10 italic"
      >
        {{ $t('KANBAN.LOSS_REASON_MODAL.EMPTY') }}
      </p>
    </div>
  </Teleport>
</template>
